import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Archive, FileText } from 'lucide-react';

// Helper: truncate candidate name to 30 characters with ellipsis
const truncateName = (firstName, lastName) => {
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();
    if (fullName.length <= 30) return fullName;
    return fullName.substring(0, 30) + '...';
};

const CandidateDetailsHeader = ({ 
    candidateData, 
    navigate, 
    onGenerateInvite, 
    onExpireInvite, 
    onDeleteCandidate,
    onMultiJDSchedule,
    onOpenTimeline,
    resumeUrl 
}) => {
    const { t } = useTranslation();
    
    const fullName = candidateData?.first_name && candidateData?.last_name 
        ? `${candidateData.first_name} ${candidateData.last_name}`
        : null;
    const displayName = fullName ? truncateName(candidateData.first_name, candidateData.last_name) : t('candidateDetails.title');
    
    const userRole = localStorage.getItem('role');

    return (
        <div className="relative overflow-hidden rounded-xl shadow-sm" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}>
            <div className="relative flex flex-wrap justify-between items-center px-4 py-3 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg transition-all duration-200 flex-shrink-0 border border-white/20 hover:border-white/40 shadow-sm"
                    >
                        <ArrowLeft size={18} className="text-white" />
                    </button>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-lg font-semibold text-white">
                                {displayName}
                            </h1>
                            {userRole === 'Super Admin' && candidateData?.company?.[0]?.name && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-white/25 text-white rounded border border-white/30 backdrop-blur-sm tracking-wide uppercase">
                                    Company: {candidateData.company[0].name}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-blue-100 mt-0.5">
                            {candidateData?.email || t('candidateDetails.subtitle')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {candidateData?.inviteStatus && (
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm border ${
                            candidateData?.inviteStatus === 'Active' 
                                ? 'border-green-300/50 bg-green-500/20 text-white' 
                                : 'border-white/30 bg-white/10 text-white'
                        }`}>
                            {candidateData?.inviteStatus === 'Active' ? t('candidateDetails.active') : candidateData?.inviteStatus}
                        </span>
                    )}
                    
                    <div className="flex items-center gap-1.5">
                        {/* Multi-JD Schedule Button */}
                        <button
                            onClick={onMultiJDSchedule}
                            className="p-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg transition-all duration-200 border border-white/20 hover:border-white/40 shadow-sm"
                            title={t('candidateDetails.multiJDScheduleBtn')}
                        >
                            <Plus size={16} />
                        </button>

                        {/* View Resume Button */}
                        {resumeUrl && (
                            <a 
                                href={resumeUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg transition-all duration-200 border border-white/20 hover:border-white/40 shadow-sm"
                                title={t('candidateDetails.viewResume')}
                            >
                                <FileText size={16} />
                            </a>
                        )}
                        
                        {candidateData?.inviteStatus === 'Active' && candidateData?.inviteToken && (
                            <button
                                onClick={() => onExpireInvite(candidateData?.inviteToken)}
                                className="p-2 bg-red-500/20 hover:bg-red-500/30 backdrop-blur-sm text-white rounded-lg transition-all duration-200 border border-red-300/30 hover:border-red-300/50 shadow-sm"
                                title={t('candidateDetails.expireInvite')}
                            >
                                <Archive size={16} />
                            </button>
                        )}
                        
                        <button
                            onClick={onDeleteCandidate}
                            className="p-2 bg-red-500/20 hover:bg-red-500/30 backdrop-blur-sm text-white rounded-lg transition-all duration-200 border border-red-300/30 hover:border-red-300/50 shadow-sm"
                            title={t('candidateDetails.archiveCandidate')}
                        >
                            <Archive size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CandidateDetailsHeader;